import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@sc/data-access-environment';
import {
  AppointmentSuggestionsApi,
  BookAppointmentApi,
  BookAppointmentApiResponse,
  CancelAppointmentApi,
  Dossier,
  Environment,
  ModifyAppointmentApi,
  ModifyAppointmentApiResponse,
  RequestAppointmentApi,
  RequestAppointmentApiResponse,
} from '@sc/data-access-models';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppointmentsHttpService {
  private readonly bookAppointmentUrl = '/api/dossier/appointment/book';
  private readonly requestAppointmentUrl = '/api/dossier/appointment/request';
  private readonly cancelAppointmentUrl = '/api/dossier/appointment/cancel';
  private readonly modifyAppointmentUrl = '/api/dossier/appointment/modify';
  private readonly fetchFreeSlotsUrl = '/api/dossier/appointment/get';
  private readonly appointmentSuggestionsUrl = `/api/accounts/sendSuggestions`;

  constructor(
    private readonly httpClient: HttpClient,
    @Inject(ENV_CONFIG) private readonly env: Environment
  ) {}

  fetchFreeSlots(params: any) {
    return this.httpClient.post<any>(this.fetchFreeSlotsUrl, params, {
      headers: {
        requestName: 'appointments-http fetchFreeSlots',
        timeout: '15000',
      },
    });
  }

  cancelAppointment(params: CancelAppointmentApi) {
    return this.httpClient.post<{
      dossier: Dossier;
      dossierForContractManager: any;
    }>(this.cancelAppointmentUrl, params, {
      headers: { requestName: 'appointments-http cancelAppointment' },
    });
  }

  modifyAppointment(params: ModifyAppointmentApi) {
    return this.httpClient.post<ModifyAppointmentApiResponse>(
      this.modifyAppointmentUrl,
      params,
      {
        headers: { requestName: 'appointments-http modifyAppointment' },
      }
    );
  }

  bookAppointment(
    params: BookAppointmentApi
  ): Observable<BookAppointmentApiResponse> {
    const dossier = JSON.parse(JSON.stringify(params.dossier));
    const requestBody: any = {
      dossier: {
        ...dossier,
        claim: dossier?.claim
          ? {
              ...dossier.claim,
              documents: dossier?.claim?.documents
                ? dossier.claim.documents.map((picture) => ({
                    type: picture.type,
                    name: picture.name,
                    metadata: picture.metadata,
                  }))
                : null,
            }
          : null,
      },
    };

    // Include Google conversion tracking if provided
    if (params.googleConversionTracking) {
      requestBody.googleConversionTracking = params.googleConversionTracking;
    }

    return this.httpClient.post<BookAppointmentApiResponse>(
      this.bookAppointmentUrl,
      requestBody,
      {
        headers: {
          requestName: 'appointments-http bookAppointment',
          timeout: '30000',
        },
      }
    );
  }

  requestAppointment(
    params: RequestAppointmentApi
  ): Observable<RequestAppointmentApiResponse> {
    const dossier = JSON.parse(JSON.stringify(params.dossier));

    return this.httpClient.post<RequestAppointmentApiResponse>(
      this.requestAppointmentUrl,
      {
        dossier: {
          ...dossier,
          claim: dossier?.claim
            ? {
                ...dossier.claim,
                documents: dossier?.claim?.documents
                  ? dossier.claim.documents.map((picture) => ({
                      type: picture.type,
                      name: picture.name,
                      metadata: picture.metadata,
                    }))
                  : null,
              }
            : null,
        },
      },
      {
        headers: {
          requestName: 'appointments-http requestAppointment',
          timeout: '30000',
        },
      }
    );
  }

  sendAppointmentSuggestions(params: AppointmentSuggestionsApi) {
    return this.httpClient.post(this.appointmentSuggestionsUrl, params, {
      headers: { requestName: 'sms-http sendAppointmentSuggestions' },
    });
  }
}
